/**
 * The sub-fab crew: facilities technicians, dressed for the room they are in.
 *
 * The same two rigged bodies as the cleanroom technicians (people.ts), with
 * the opposite dressing - because the sub-fab is NOT a cleanroom, and the
 * change of clothes at the bottom of the stair is the lesson. No hood, no
 * mask, no shoe covers: a fitted coverall, a high-visibility vest, a hard
 * hat under the pipe racks, safety glasses, work gloves, boots, a tool belt,
 * and ear defenders on whoever is standing beside the pumps.
 *
 * THE COVERALL IS A SHADER, like the gown upstairs. The bodies are one
 * skin-tight mesh with one material, so the coverall is that mesh pushed
 * out a centimetre and painted, with everything above the neck left as
 * skin - which is why the face shows and the hands get gloves attached
 * rather than painted. Everything worn is parented to a bone and moves with
 * the limb.
 *
 * Kept apart from people.ts on purpose: the two files share nothing but the
 * model URLs, and this one owns the sub-fab's posts and lane (from the
 * layout module, where the harness can hold them standable and clear).
 */

import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js'
import { CREW_POSTS, CREW_WALK, SUBFAB, type CrewPost } from './subfabLayout'
import { captureRestPose, relaxIdlePose, type RestPose } from './relaxIdle'

type Body = 'f' | 'm'

export interface CrewMember {
  group: THREE.Group
  mixer: THREE.AnimationMixer
  walker: { dir: 1 | -1; stopped: boolean } | null
  timer: number
  clips: Record<string, THREE.AnimationAction>
  current: string
  /** The hands as modelled, for the idle relaxation (relaxIdle.ts). */
  rest: RestPose
}

export interface SubfabCrew {
  group: THREE.Group
  members: CrewMember[]
  advance(dt: number, eye: THREE.Vector3): void
  colliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }>
}

/** How far the coverall stands off the body: fitted, not bagged. */
const COVERALL_FIT = 0.012

const SKIN = new THREE.Color(0xc99a7a)
const COVERALL = new THREE.Color(0x2b3a4f)

/**
 * The coverall: the body mesh, pushed out along its normals below the neck
 * and painted, with the head left as skin. `neckY` is the neck bone's height
 * in the model's own bind pose, so each body gets its own line.
 */
function coverallMaterial(neckY: number): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: COVERALL, roughness: 0.85, metalness: 0 })
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uNeckY = { value: neckY }
    shader.uniforms.uSkin = { value: SKIN }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying float vBindY;\nuniform float uNeckY;')
      .replace(
        '#include <begin_vertex>',
        [
          '#include <begin_vertex>',
          'vBindY = position.y;',
          `transformed += objectNormal * (position.y > uNeckY ? 0.0 : ${COVERALL_FIT.toFixed(3)});`,
        ].join('\n'),
      )
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vBindY;\nuniform float uNeckY;\nuniform vec3 uSkin;')
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\nif (vBindY > uNeckY) diffuseColor.rgb = uSkin;',
      )
  }
  m.customProgramCacheKey = () => `coverall-${neckY.toFixed(3)}`
  return m
}

const VEST = new THREE.MeshStandardMaterial({ color: 0xd7f04a, roughness: 0.8 })
const VEST_BAND = new THREE.MeshStandardMaterial({ color: 0xbfc4c8, roughness: 0.3, metalness: 0.2 })
const HAT = new THREE.MeshStandardMaterial({ color: 0xf2f4f6, roughness: 0.4 })
const HAT_YELLOW = new THREE.MeshStandardMaterial({ color: 0xf0c020, roughness: 0.4 })
const LENS = new THREE.MeshStandardMaterial({ color: 0x1f262e, roughness: 0.15, metalness: 0.4, transparent: true, opacity: 0.7 })
const GLOVE = new THREE.MeshStandardMaterial({ color: 0xb07a3c, roughness: 0.85 })
const BOOT = new THREE.MeshStandardMaterial({ color: 0x3a2a1c, roughness: 0.8 })
const BELT = new THREE.MeshStandardMaterial({ color: 0x4a3524, roughness: 0.8 })
const MUFF = new THREE.MeshStandardMaterial({ color: 0xe0562a, roughness: 0.6 })

/** Fetch and dress the crew. Resolves to nothing if no model loads. */
export async function loadSubfabCrew(urls: Record<Body, string>): Promise<SubfabCrew | null> {
  const loader = new GLTFLoader()
  const loaded: Partial<Record<Body, GLTF>> = {}
  await Promise.all(
    (Object.keys(urls) as Body[]).map(async (body) => {
      try {
        loaded[body] = await loader.loadAsync(urls[body])
      } catch (err) {
        console.warn(`crew model ${body} unavailable:`, err)
      }
    }),
  )
  const any = loaded.f ?? loaded.m
  if (!any) return null
  const g = new THREE.Group()
  const members: CrewMember[] = []
  const coveralls: Partial<Record<Body, THREE.MeshStandardMaterial>> = {}

  const make = (body: Body, loud: boolean, hatColour: THREE.Material): CrewMember => {
    const gltf = loaded[body] ?? any
    const model = cloneSkeleton(gltf.scene) as THREE.Group
    model.updateMatrixWorld(true)
    const at = (name: string): THREE.Vector3 | null => {
      const bone = model.getObjectByName(name)
      return bone ? bone.getWorldPosition(new THREE.Vector3()) : null
    }
    const neck = at('neck_01')
    const neckY = neck ? neck.y - 0.01 : 1.5
    coveralls[body] ??= coverallMaterial(neckY)

    const eyeBox = new THREE.Box3()
    model.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!mesh.isMesh) return
      mesh.frustumCulled = false
      if (/eye/i.test((mesh.material as THREE.Material).name)) {
        // The eyes stay: there is no hood to hide them in.
        eyeBox.expandByObject(mesh)
      } else {
        mesh.material = coveralls[body]!
        mesh.castShadow = true
      }
    })
    const eyes = eyeBox.getCenter(new THREE.Vector3())
    const group = new THREE.Group()
    group.add(model)
    const mixer = new THREE.AnimationMixer(model)
    const clips: Record<string, THREE.AnimationAction> = {}
    for (const clip of gltf.animations) clips[clip.name] = mixer.clipAction(clip)

    const attach = (boneName: string, mesh: THREE.Mesh, position: THREE.Vector3, forward: THREE.Vector3): void => {
      const bone = model.getObjectByName(boneName)
      if (!bone) return
      mesh.position.copy(position)
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), forward.clone().normalize())
      group.add(mesh)
      bone.attach(mesh)
    }
    const ahead = new THREE.Vector3(0, 0, 1)

    // The hard hat: a shell over the crown with a brim, and the glasses
    // across the eyes. Both ride the head bone.
    const crown = eyes.clone().add(new THREE.Vector3(0, 0.075, -0.015))
    const shell = new THREE.Mesh(new THREE.SphereGeometry(0.125, 18, 10, 0, Math.PI * 2, 0, Math.PI * 0.52), hatColour)
    shell.scale.set(1, 0.85, 1.08)
    attach('Head', shell, crown, ahead)
    const brim = new THREE.Mesh(new THREE.TorusGeometry(0.125, 0.012, 6, 24), hatColour)
    brim.geometry.rotateX(Math.PI / 2)
    attach('Head', brim, crown.clone().add(new THREE.Vector3(0, -0.005, 0)), ahead)
    const peak = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.008, 0.05), hatColour)
    attach('Head', peak, crown.clone().add(new THREE.Vector3(0, -0.005, 0.145)), ahead)
    attach('Head', new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.032, 0.012), LENS), eyes.clone().add(new THREE.Vector3(0, 0, 0.058)), ahead)
    if (loud) {
      // Ear defenders: a cup each side, and the band over the hat.
      for (const side of [-1, 1]) {
        const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.038, 0.03, 12), MUFF)
        cup.geometry.rotateZ(Math.PI / 2)
        attach('Head', cup, eyes.clone().add(new THREE.Vector3(side * 0.088, -0.01, -0.02)), ahead)
      }
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.008, 6, 20, Math.PI), MUFF)
      attach('Head', band, eyes.clone().add(new THREE.Vector3(0, 0.0, -0.02)), ahead)
    }

    // The vest round the chest with its reflective band; the belt at the hips.
    const chest = at('spine_03')
    const hips = at('spine_01')
    if (chest) {
      const vest = new THREE.Mesh(new THREE.CylinderGeometry(0.205, 0.19, 0.36, 18, 1, true), VEST)
      vest.material.side = THREE.DoubleSide
      attach('spine_03', vest, chest.clone().add(new THREE.Vector3(0, 0.02, 0)), ahead)
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.212, 0.212, 0.04, 18, 1, true), VEST_BAND)
      band.material.side = THREE.DoubleSide
      attach('spine_03', band, chest.clone().add(new THREE.Vector3(0, -0.06, 0)), ahead)
    }
    if (hips) {
      const belt = new THREE.Mesh(new THREE.TorusGeometry(0.175, 0.02, 8, 24), BELT)
      belt.geometry.rotateX(Math.PI / 2)
      attach('spine_01', belt, hips.clone().add(new THREE.Vector3(0, 0.03, 0)), ahead)
      const pouch = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.12, 0.06), BELT)
      attach('spine_01', pouch, hips.clone().add(new THREE.Vector3(0.16, -0.05, 0.03)), ahead)
    }
    for (const side of ['l', 'r'] as const) {
      const wrist = at(`hand_${side}`)
      const knuckle = at(`middle_01_${side}`)
      if (wrist && knuckle) {
        const glove = new THREE.Mesh(new THREE.SphereGeometry(1, 12, 8), GLOVE)
        glove.scale.set(0.056, 0.036, 0.092)
        attach(`hand_${side}`, glove, wrist.clone().lerp(knuckle, 0.6), knuckle.clone().sub(wrist))
      }
      const ankle = at(`foot_${side}`)
      const ball = at(`ball_${side}`)
      if (ankle && ball) {
        const boot = new THREE.Mesh(new THREE.SphereGeometry(1, 14, 10), BOOT)
        boot.scale.set(0.068, 0.055, 0.155)
        attach(`foot_${side}`, boot, ankle.clone().lerp(ball, 0.6).add(new THREE.Vector3(0, -0.03, 0)), ball.clone().sub(ankle))
      }
    }
    group.scale.setScalar(body === 'f' ? 0.93 : 1.0)
    return { group, mixer, walker: null, timer: 0, clips, current: '', rest: captureRestPose(model) }
  }
  const play = (p: CrewMember, name: string, fade = 0.4): void => {
    const next = p.clips[name]
    if (!next || p.current === name) return
    const prev = p.clips[p.current]
    next.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).fadeIn(fade).play()
    if (prev) prev.fadeOut(fade)
    p.current = name
  }

  const floorY = -SUBFAB.depth
  const place = (post: CrewPost, i: number): void => {
    const p = make(post.body, post.loud, i % 2 === 0 ? HAT : HAT_YELLOW)
    p.group.position.set(post.x, floorY, post.z)
    p.group.rotation.y = post.yaw
    p.timer = Math.random() * 6
    play(p, post.clip, 0)
    p.mixer.update(Math.random() * 2)
    g.add(p.group)
    members.push(p)
  }
  CREW_POSTS.forEach(place)

  // The one on rounds, starting mid-walkway.
  {
    const p = make('f', false, HAT)
    p.walker = { dir: 1, stopped: false }
    p.group.position.set((CREW_WALK.minX + CREW_WALK.maxX) / 2, floorY, CREW_WALK.z)
    p.group.rotation.y = Math.PI / 2
    play(p, 'Walk_Loop', 0)
    p.mixer.update(0.4)
    g.add(p.group)
    members.push(p)
  }

  const advance = (dt: number, eye: THREE.Vector3): void => {
    for (const p of members) {
      p.timer += dt
      if (p.walker) {
        const w = p.walker
        // Stop for a student in the way, a couple of metres ahead - only a
        // student down here; one on the floor above is not in the way.
        const ax = p.group.position.x + w.dir * 1.6
        const dx = eye.x - ax
        const dz = eye.z - CREW_WALK.z
        const blocked = eye.y < 0 && dx * dx + dz * dz < 1.8 * 1.8
        if (blocked !== w.stopped) {
          w.stopped = blocked
          play(p, blocked ? 'Idle_Loop' : 'Walk_Loop', 0.3)
        }
        if (!w.stopped) {
          p.group.position.x += w.dir * CREW_WALK.speed * dt
          if (p.group.position.x > CREW_WALK.maxX) w.dir = -1
          if (p.group.position.x < CREW_WALK.minX) w.dir = 1
          const target = w.dir > 0 ? Math.PI / 2 : -Math.PI / 2
          let d = target - p.group.rotation.y
          d = Math.atan2(Math.sin(d), Math.cos(d))
          p.group.rotation.y += Math.sign(d) * Math.min(Math.abs(d), 2.5 * dt)
        }
      } else if (p.timer > 9) {
        p.timer = 0
        const order = ['Interact', 'Idle_Talking_Loop', 'Idle_Loop']
        const i = (order.indexOf(p.current) + 1) % order.length
        play(p, order[i])
      }
      p.mixer.update(dt)
      // And then the idle's fists and elbows come down - see relaxIdle.ts.
      // Scaled by what the idle clip is actually contributing, so the walk
      // keeps its swing and the interact keeps its hand on the panel.
      relaxIdlePose(p.group, p.rest, p.clips.Idle_Loop?.getEffectiveWeight() ?? 0)
    }
  }

  const colliders = () =>
    members
      .filter((p) => !p.walker)
      .map((p) => ({
        minX: p.group.position.x - 0.35,
        maxX: p.group.position.x + 0.35,
        minZ: p.group.position.z - 0.35,
        maxZ: p.group.position.z + 0.35,
      }))

  return { group: g, members, advance, colliders }
}
